import { customSubmitClaim } from "./index.mjs";
import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { validateField } from "commonAssets/lib/validateField";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import { loading, outputDiv } from "commonAssets/js/constants";
import nlcmFormDefault from "../../docs/nlcmFormDefault.json";
import claimDeadlineAlertBody from "../../docs/html/claim_deadline_alert_body.html?raw";
import claimDeadlineAlertForm from "../../docs/html/claim_deadline_alert_form.html?raw";
import generalInstructionsBody from "../../docs/html/general_instructions_body.html?raw";
import contactInformationBody from "../../docs/html/contact_information_body.html?raw";
import { modal } from "commonAssets/lib/modal";
import { classMemberAuth, consumeNlcmAutoLogin } from "./classMemberAuth.mjs";

const nlcmHtmlAssets = {
    "./html/claim_deadline_alert_body.html": claimDeadlineAlertBody,
    "./html/claim_deadline_alert_form.html": claimDeadlineAlertForm,
    "./html/general_instructions_body.html": generalInstructionsBody,
    "./html/contact_information_body.html": contactInformationBody,
};

function createNlcmNotice() {
    return createElement(
        "div",
        {
            class: "alert alert-warning text-center text-dark",
            role: "alert",
        },
        [],
        "You have indicated that you believe you are a class member, but did not receive a Notice containing credentials to elect a payment method. Please provide all the information requested below. Providing this information does not guarantee you are a class member or that you will receive a payment from this settlement, but it will allow us to investigate further. If you do not provide all the requested information, you will not be eligible for a payment."
    );
}

/**
 * WP-1802: expired general NLCM deadline statement for the credential login view only.
 */
function createNlcmDeadlineNotice() {
    return createElement(
        "div",
        {
            class: "alert alert-warning text-center text-dark",
            role: "alert",
        },
        [],
        "The September 8, 2026 deadline for submitting any class membership evidence or election of payment method election has passed."
    );
}

export class customNlcmSubmitClaim extends customSubmitClaim {
    claim_form(params, additionalParams) {
        const form = super.claim_form(params, additionalParams);
        form.insertBefore(createNlcmNotice(), form.querySelector("#confirmation-email-alert"));
        return form;
    }

    guard(params, getDeadline) {
        const form = super.guard(params, getDeadline);
        form.insertBefore(createNlcmNotice(), form.firstChild);
        form.insertBefore(createNlcmDeadlineNotice(), form.firstChild);
        return form;
    }

    async form() {
        const baseUrl = new URL(
            "../../docs/nlcmFormDefault.json",
            window.location.href
        ).href;
        const config = await SubmitClaim.resolveHtmlFields(nlcmFormDefault, {
            baseUrl,
            assets: nlcmHtmlAssets,
        });
        const form = config.claim_form[this.lang];
        this.init_claim(form);
    }

    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            // Hide guard
                            // document.getElementById('guard').classList.add("d-none");
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    // if input#submit-guard was clicked add .was-verified to form#guard
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                            // TODO: add required text to labels
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added

                                    const auth = classMemberAuth(result.data, 'nlcm_payment_election');
                                    if (auth.action === 'error') {
                                        modal('Error', 'You are not eligible to proceed with the payment election form. If you believe this is an error, please contact support.');
                                        loadingGif(loading, "hide", 250);
                                        return;
                                    }

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });

                    // Auto-login carry-through: if a redirect from /payment-election
                    // wrote one-shot credentials, pre-fill the guard inputs and
                    // programmatically submit so the destination skips re-entry.
                    const carry = consumeNlcmAutoLogin();
                    if (carry) {
                        document.getElementById("zero_zero_id").value = carry.zero_zero_id;
                        document.getElementById("confirmation_code").value = carry.confirmation_code;
                        document.getElementById("guard").requestSubmit();
                        return;
                    }
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }
}
